import { useMemo, useState, useCallback } from 'react';
import PatientLayout from '@/components/patient/PatientLayout';
import type { PageProps } from './Show';
import { PatientSystemPromptModal, PatientPageHeader } from '@/components/patient';
import { UserRound, Bot, Bell, Settings } from 'lucide-react';
import { router } from '@inertiajs/react';
import { logError } from '@/lib/logger';
import { breadcrumbs } from '@/components/ui/breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  EmoviBotTabContent,
  NotificationsTabContent,
  ProfileTabContent,
  type ProfileContentProps,
} from '@/components/patient/profile';

// Wrapper: usa PatientLayout e riutilizza il vecchio profilo come contenuto
export type ProfilePageProps = PageProps & {
  overview: ProfileContentProps['overview'];
  clinicalNotesMeta: ProfileContentProps['clinicalNotesMeta'];
  availableFlags?: ProfileContentProps['availableFlags'];
  patient: ProfileContentProps['patient'];
};

export default function ProfilePage(props: ProfilePageProps) {
  const { patient, stats, charts, overview, clinicalNotesMeta, availableFlags, pushNotifications } =
    props;
  const patientForProfile: ProfileContentProps['patient'] = {
    ...patient,
    display_name: patient.name,
    name: patient.name,
    system_prompt: (patient as { system_prompt?: string | null }).system_prompt ?? null,
  };

  return (
    <PatientLayout patient={patient} stats={stats} charts={charts} activeTab="profile">
      <ProfileContent
        patient={patientForProfile}
        overview={overview}
        clinicalNotesMeta={clinicalNotesMeta}
        availableFlags={availableFlags}
        pushNotifications={pushNotifications}
        hideChrome
      />
    </PatientLayout>
  );
}

function ProfileContent({
  patient,
  overview,
  pushNotifications,
  availableFlags,
  hideChrome = false,
}: ProfileContentProps) {
  const [localPatient, setLocalPatient] = useState(patient);
  const [isRegeneratingPrompt, setIsRegeneratingPrompt] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const isDev = import.meta.env.DEV;
  const isDisabled = Boolean(
    (patient as { disabledAt?: string | null }).disabledAt || patient.disabled_at,
  );

  const translatedFlags = useMemo(() => {
    if (!availableFlags) return [] as string[];
    const result: string[] = [];
    overview.ai_config.flags_by_category.neurodivergence.forEach((flag) => {
      result.push(availableFlags.neurodivergence[flag] || flag);
    });
    overview.ai_config.flags_by_category.psychological_state.forEach((flag) => {
      result.push(availableFlags.psychological_state[flag] || flag);
    });
    return result;
  }, [availableFlags, overview.ai_config.flags_by_category]);

  const handleRegeneratePrompt = async () => {
    if (isRegeneratingPrompt) return;
    try {
      setIsRegeneratingPrompt(true);
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const response = await fetch(`/therapists/patients/${patient.id}/regenerate_prompt`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrf,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('response not ok');
      }

      const data = await response.json();
      if (!data?.ok) {
        throw new Error(data?.error || 'Rigenerazione non riuscita');
      }

      router.reload({
        only: ['overview', 'patient'],
        onFinish: () => setIsRegeneratingPrompt(false),
      });
    } catch (error) {
      logError('Regenerate prompt failed', error);
      window.alert('Impossibile rigenerare il prompt. Riprova più tardi.');
      setIsRegeneratingPrompt(false);
    }
  };

  const openPromptModal = useCallback(() => {
    setIsPromptModalOpen(true);
  }, []);

  const handlePromptSaved = useCallback(() => {
    router.reload({ only: ['overview', 'patient'] });
  }, []);

  const handleToggleDisabled = async () => {
    const nextDisabledAt = isDisabled ? null : new Date().toISOString();
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    setStatusLoading(true);
    setStatusError('');
    try {
      const response = await fetch(`/therapists/patients/${patient.id}.json`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrf,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ patient: { disabled_at: nextDisabledAt } }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setStatusDialogOpen(false);
      router.reload({ only: ['patient'] });
    } catch (error) {
      logError('Toggle patient disabled failed', error);
      setStatusError('Operazione non riuscita. Riprova.');
    } finally {
      setStatusLoading(false);
    }
  };

  const content = (
    <div className="mx-auto">
      {!hideChrome &&
        breadcrumbs([
          { text: 'Clienti', url: '/therapists/patients' },
          {
            text:
              patient.display_name ||
              patient.name ||
              [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim() ||
              'Cliente',
            url: `/therapists/patients/${patient.id}`,
          },
          {
            text: 'Profile',
            current: true,
          },
        ])}
      {!hideChrome && (
        <PatientPageHeader
          patient={{ ...patient, name: patient.name || undefined }}
          active="profile"
        />
      )}

      <section className="space-y-2">
        <Tabs defaultValue="profilo" className="flex flex-col gap-4">
          <div className="flex">
            <TabsList className="w-full justify-start gap-2 overflow-x-auto">
              <TabsTrigger value="profilo" className="gap-2">
                <UserRound className="h-4 w-4" />
                Profilo cliente
              </TabsTrigger>
              <TabsTrigger value="emovibot" className="gap-2">
                <Bot className="h-4 w-4" />
                EmoviBot
              </TabsTrigger>
              <TabsTrigger value="notifiche" className="gap-2">
                <Bell className="h-4 w-4" />
                Notifiche
              </TabsTrigger>
              <TabsTrigger value="impostazioni" className="gap-2">
                <Settings className="h-4 w-4" />
                Impostazioni
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="profilo" className="space-y-4">
            <ProfileTabContent
              patient={patient}
              localPatient={localPatient}
              overview={overview}
              translatedFlags={translatedFlags}
              availableFlags={availableFlags}
              onPatientUpdated={setLocalPatient}
            />
          </TabsContent>

          <TabsContent value="emovibot" className="space-y-4">
            <EmoviBotTabContent
              patient={patient}
              localPatient={localPatient}
              overview={overview}
              translatedFlags={translatedFlags}
              isDev={isDev}
              isRegeneratingPrompt={isRegeneratingPrompt}
              onRegeneratePrompt={handleRegeneratePrompt}
              onOpenPromptModal={openPromptModal}
            />
          </TabsContent>

          <TabsContent value="notifiche" className="space-y-4">
            <NotificationsTabContent patientId={patient.id} pushNotifications={pushNotifications} />
          </TabsContent>

          <TabsContent value="impostazioni" className="space-y-4">
            <Card variant="outlined" semantic="destructive">
              <CardHeader>
                <CardTitle>Accesso alla piattaforma</CardTitle>
                <CardDescription>
                  Disattiva il cliente per bloccare l&apos;accesso. Puoi riattivarlo in qualsiasi momento.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {statusError ? <p className="text-sm text-destructive">{statusError}</p> : null}
              </CardContent>
              <CardFooter className="gap-2">
                <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant={isDisabled ? 'outline' : 'destructive'}>
                      {isDisabled ? 'Riattiva' : 'Disattiva'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {isDisabled ? 'Riattivare il cliente?' : 'Disattivare il cliente?'}
                      </DialogTitle>
                      <DialogDescription>
                        {isDisabled
                          ? 'Il cliente potrà accedere di nuovo alla piattaforma.'
                          : 'Il cliente non potrà più accedere alla piattaforma finché non lo riattivi.'}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                      <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button
                        variant={isDisabled ? 'default' : 'destructive'}
                        onClick={handleToggleDisabled}
                        disabled={statusLoading}
                      >
                        {statusLoading ? 'Attendi...' : isDisabled ? 'Riattiva' : 'Disattiva'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );

  return (
    <>
      {content}
      {isDev && (
        <PatientSystemPromptModal
          open={isPromptModalOpen}
          patientId={patient.id}
          onOpenChange={setIsPromptModalOpen}
          onSaved={handlePromptSaved}
        />
      )}
    </>
  );
}
